import { supabase } from '../lib/supabase';
import { createUserWithRole } from './callEdge';
import type { User, Project, ProjectStatus, ProjectDomain, ProfileType } from '../types';

interface TestUser {
  id: string;
  email: string;
  password: string;
  profile_type: ProfileType;
  full_name: string;
  phone?: string;
  organization?: string;
  address?: string;
  city?: string;
  postal_code?: string;
}

interface TestProject {
  name: string;
  description: string;
  status: ProjectStatus;
  domain: ProjectDomain;
  owner_email: string;
  assigned_advisor_email?: string;
  funding_requested?: number;
  funding_amount?: number;
  start_date?: string;
  end_date?: string;
}

const TEST_USERS: TestUser[] = [
  // Porteurs de projet
  {
    id: '550e8400-e29b-41d4-a716-446655440001',
    email: 'marie.joseph@test.com',
    password: 'password123',
    profile_type: 'PORTEUR',
    full_name: 'Marie-Claire Joseph',
    phone: '0590 12 34 56',
    organization: 'Coopérative Agricole Gwada',
    address: '15 Rue des Palmiers',
    city: 'Basse-Terre',
    postal_code: '97100'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440002',
    email: 'jean.baptiste@test.com',
    password: 'password123',
    profile_type: 'PORTEUR',
    full_name: 'Jean-Baptiste Moreau',
    phone: '0590 23 45 67',
    organization: 'Association Jeunesse Culturelle',
    address: '8 Avenue de la République',
    city: 'Pointe-à-Pitre',
    postal_code: '97110'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440003',
    email: 'lucie.moreau@test.com',
    password: 'password123',
    profile_type: 'PORTEUR',
    full_name: 'Lucie Moreau',
    phone: '0590 34 56 78',
    organization: 'Éco-Solidaire 971',
    address: '22 Rue Victor Hugo',
    city: 'Les Abymes',
    postal_code: '97139'
  },

  // Accompagnateurs
  {
    id: '550e8400-e29b-41d4-a716-446655440010',
    email: 'sophie.martin@cress-guadeloupe.org',
    password: 'password123',
    profile_type: 'ACCOMPAGNATEUR',
    full_name: 'Sophie Martin',
    phone: '0590 45 67 89',
    organization: 'CRESS Guadeloupe',
    address: '12 Rue de la Liberté',
    city: 'Basse-Terre',
    postal_code: '97100'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440011',
    email: 'pierre.dubois@ess-conseil.gp',
    password: 'password123',
    profile_type: 'ACCOMPAGNATEUR',
    full_name: 'Pierre Dubois',
    phone: '0590 56 78 90',
    organization: 'ESS Conseil Guadeloupe',
    address: '5 Boulevard Chanzy',
    city: 'Pointe-à-Pitre',
    postal_code: '97110'
  },
  {
    id: '550e8400-e29b-41d4-a716-446655440012',
    email: 'isabelle.leroy@accompagnement-ess.gp',
    password: 'password123',
    profile_type: 'ACCOMPAGNATEUR',
    full_name: 'Isabelle Leroy',
    phone: '0590 67 89 01',
    organization: 'Accompagnement ESS',
    address: '18 Rue Schoelcher',
    city: 'Le Gosier',
    postal_code: '97190'
  },

  // Coordinateur
  {
    id: '550e8400-e29b-41d4-a716-446655440020',
    email: 'jean.coordinator@cress-guadeloupe.org',
    password: 'password123',
    profile_type: 'COORDINATEUR',
    full_name: 'Jean Coordinator',
    phone: '0590 78 90 12',
    organization: 'CRESS Guadeloupe',
    address: '12 Rue de la Liberté',
    city: 'Basse-Terre',
    postal_code: '97100'
  },

  // Financeur
  {
    id: '550e8400-e29b-41d4-a716-446655440030',
    email: 'paul.financeur@financement-ess.gp',
    password: 'password123',
    profile_type: 'FINANCEUR',
    full_name: 'Paul Financeur',
    phone: '0590 89 01 23',
    organization: 'Financement ESS Guadeloupe',
    address: '25 Rue du Commerce',
    city: 'Baie-Mahault',
    postal_code: '97122'
  },

  // Administrateur
  {
    id: '550e8400-e29b-41d4-a716-446655440040',
    email: 'admin@cress-guadeloupe.org',
    password: 'password123',
    profile_type: 'ADMIN',
    full_name: 'Admin System',
    phone: '0590 90 12 34',
    organization: 'CRESS Guadeloupe',
    address: '12 Rue de la Liberté',
    city: 'Basse-Terre',
    postal_code: '97100'
  }
];

const TEST_PROJECTS: TestProject[] = [
  {
    name: 'Ferme Agroécologique des Antilles',
    description: 'Développement d\'une ferme agroécologique produisant des légumes biologiques pour les circuits courts en Guadeloupe.',
    status: 'EN_COURS',
    domain: 'AGRICULTURE',
    owner_email: 'marie.joseph@test.com',
    assigned_advisor_email: 'sophie.martin@cress-guadeloupe.org',
    funding_requested: 150000,
    funding_amount: 120000,
    start_date: '2024-01-15',
    end_date: '2025-12-31'
  },
  {
    name: 'Centre Culturel Jeunesse Gwada',
    description: 'Création d\'un centre culturel dédié à la jeunesse avec ateliers artistiques et formations.',
    status: 'VALIDE',
    domain: 'CULTURE',
    owner_email: 'jean.baptiste@test.com',
    assigned_advisor_email: 'pierre.dubois@ess-conseil.gp',
    funding_requested: 80000,
    funding_amount: 75000,
    start_date: '2024-02-01',
    end_date: '2025-01-31'
  },
  {
    name: 'Recyclerie Solidaire Basse-Terre',
    description: 'Mise en place d\'une recyclerie solidaire pour la réduction des déchets et l\'insertion professionnelle.',
    status: 'SOUMIS',
    domain: 'ENVIRONNEMENT',
    owner_email: 'lucie.moreau@test.com',
    assigned_advisor_email: 'isabelle.leroy@accompagnement-ess.gp',
    funding_requested: 45000,
    start_date: '2024-03-01',
    end_date: '2024-12-31'
  },
  {
    name: 'Service Aide à Domicile Solidaire',
    description: 'Service d\'aide à domicile pour personnes âgées et en situation de handicap.',
    status: 'TERMINE',
    domain: 'SOCIAL',
    owner_email: 'marie.joseph@test.com',
    assigned_advisor_email: 'sophie.martin@cress-guadeloupe.org',
    funding_requested: 95000,
    funding_amount: 85000,
    start_date: '2023-06-01',
    end_date: '2024-05-31'
  },
  {
    name: 'Atelier Textile Coopératif',
    description: 'Coopérative de production textile utilisant des matières premières locales et équitables.',
    status: 'EN_COURS',
    domain: 'ARTISANAT',
    owner_email: 'marie.joseph@test.com',
    assigned_advisor_email: 'pierre.dubois@ess-conseil.gp',
    funding_requested: 65000,
    funding_amount: 60000,
    start_date: '2024-01-01',
    end_date: '2024-12-31'
  },
  {
    name: 'Formation Numérique Seniors',
    description: 'Programme de formation au numérique pour les seniors de Guadeloupe.',
    status: 'ACCORD_PROVISOIRE',
    domain: 'FORMATION',
    owner_email: 'marie.joseph@test.com',
    assigned_advisor_email: 'isabelle.leroy@accompagnement-ess.gp',
    funding_requested: 35000,
    start_date: '2024-04-01',
    end_date: '2024-11-30'
  },
  {
    name: 'Épicerie Solidaire Mobile',
    description: 'Camion épicerie solidaire pour desservir les zones rurales isolées.',
    status: 'VALIDE',
    domain: 'COMMERCE',
    owner_email: 'marie.joseph@test.com',
    assigned_advisor_email: 'sophie.martin@cress-guadeloupe.org',
    funding_requested: 75000,
    funding_amount: 70000,
    start_date: '2024-05-01',
    end_date: '2025-04-30'
  }
];

export class TestDataGenerator {
  static async createTestUsers(): Promise<void> {
    console.log('🔄 Création des utilisateurs de test...');
    
    for (const testUser of TEST_USERS) {
      try {
        // Créer l'utilisateur via Edge Function admin
        const result = await createUserWithRole(
          testUser.email,
          testUser.password,
          {
            role: testUser.profile_type.toLowerCase(),
            metadata: {
              full_name: testUser.full_name,
              phone: testUser.phone,
              organization: testUser.organization
            },
            profile_data: {
              profile_type: testUser.profile_type,
              full_name: testUser.full_name,
              phone: testUser.phone,
              organization: testUser.organization,
              address: testUser.address,
              city: testUser.city,
              postal_code: testUser.postal_code
            }
          },
          import.meta.env.VITE_SUPABASE_URL,
          import.meta.env.VITE_SUPABASE_ANON_KEY
        );

        console.log(`✅ Utilisateur créé: ${testUser.email} (${testUser.profile_type})`);
      } catch (error: any) {
        if (error.message?.includes('User already registered') || 
            error.message?.includes('already exists')) {
          console.log(`⚠️ Utilisateur existe déjà: ${testUser.email}`);
          
          // Mettre à jour le profil existant si nécessaire
          try {
            const { error: profileError } = await supabase
              .from('users')
              .upsert({
                id: testUser.id,
                profile_type: testUser.profile_type,
                full_name: testUser.full_name,
                phone: testUser.phone,
                organization: testUser.organization,
                address: testUser.address,
                city: testUser.city,
                postal_code: testUser.postal_code
              });

            if (profileError) {
              console.warn(`Erreur mise à jour profil pour ${testUser.email}:`, profileError);
            }
          } catch (profileError) {
            console.warn(`Erreur profil pour ${testUser.email}:`, profileError);
          }
        } else {
          console.error(`❌ Erreur pour ${testUser.email}:`, error.message);
        }
      }
    }
  }

  static async createTestProjects(): Promise<void> {
    console.log('🔄 Création des projets de test...');
    
    // Récupérer les IDs des utilisateurs
    const { data: users, error: usersError } = await supabase
      .from('users')
      .select('id');

    if (usersError) {
      throw usersError;
    }

    // Create user map using auth.users instead
    const { data: authUsers } = await supabase.auth.admin.listUsers();
    const userMap = new Map(authUsers?.users?.map(u => [u.email, u.id]) || []);

    for (const testProject of TEST_PROJECTS) {
      try {
        const ownerId = userMap.get(testProject.owner_email);
        const advisorId = testProject.assigned_advisor_email 
          ? userMap.get(testProject.assigned_advisor_email) 
          : null;

        if (!ownerId) {
          console.warn(`❌ Propriétaire non trouvé: ${testProject.owner_email}`);
          continue;
        }

        const { data: project, error: projectError } = await supabase
          .from('projects')
          .insert({
            name: testProject.name,
            description: testProject.description,
            status: testProject.status,
            domain: testProject.domain,
            owner_id: ownerId,
            assigned_advisor_id: advisorId,
            funding_requested: testProject.funding_requested,
            funding_amount: testProject.funding_amount,
            start_date: testProject.start_date,
            end_date: testProject.end_date
          })
          .select()
          .single();

        if (projectError) {
          console.warn(`❌ Erreur projet ${testProject.name}:`, projectError);
          continue;
        }

        console.log(`✅ Projet créé: ${testProject.name}`);

        // Créer des étapes de progression pour le projet
        await TestDataGenerator.createProjectStages(project.id, testProject.status);

        // Créer quelques documents de test
        await TestDataGenerator.createTestDocuments(project.id, ownerId);

        // Créer des rendez-vous si le projet a un accompagnateur
        if (advisorId) {
          await TestDataGenerator.createTestAppointments(project.id, ownerId, advisorId);
        }

      } catch (error) {
        console.error(`❌ Erreur pour le projet ${testProject.name}:`, error);
      }
    }
  }

  static async createProjectStages(projectId: string, currentStatus: ProjectStatus): Promise<void> {
    const statusProgression: ProjectStatus[] = [
      'A_PREPARER',
      'SOUMIS',
      'ACCORD_PROVISOIRE',
      'VALIDE',
      'DEBLOCAGE_FONDS',
      'EN_COURS',
      'VERIFICATION_FINALE',
      'TERMINE'
    ];

    const currentIndex = statusProgression.indexOf(currentStatus);
    if (currentIndex === -1) return;

    const { data: { user } } = await supabase.auth.getUser();
    const setById = user?.id || TEST_USERS[0].id;

    // Créer les étapes jusqu'au statut actuel
    for (let i = 0; i <= currentIndex; i++) {
      const status = statusProgression[i];
      const reachedAt = new Date();
      reachedAt.setDate(reachedAt.getDate() - (currentIndex - i) * 7); // Une étape par semaine

      try {
        await supabase
          .from('project_stages')
          .insert({
            project_id: projectId,
            status,
            reached_at: reachedAt.toISOString(),
            set_by_id: setById,
            note: `Passage automatique au statut ${status}`
          });
      } catch (error) {
        console.warn(`Erreur création étape ${status}:`, error);
      }
    }
  }

  static async createTestDocuments(projectId: string, uploaderId: string): Promise<void> {
    const testDocuments = [
      {
        filename: 'kbis_entreprise.pdf',
        document_type: 'KBIS',
        file_size: 1024 * 1024 * 2, // 2MB
        mime_type: 'application/pdf'
      },
      {
        filename: 'rib_compte_principal.pdf',
        document_type: 'RIB',
        file_size: 1024 * 512, // 512KB
        mime_type: 'application/pdf'
      },
      {
        filename: 'description_projet_detaillee.pdf',
        document_type: 'DESCRIPTION',
        file_size: 1024 * 1024 * 5, // 5MB
        mime_type: 'application/pdf'
      }
    ];

    for (const doc of testDocuments) {
      try {
        await supabase
          .from('documents')
          .insert({
            project_id: projectId,
            filename: doc.filename,
            file_url: `https://example.com/documents/${doc.filename}`,
            file_size: doc.file_size,
            mime_type: doc.mime_type,
            document_type: doc.document_type,
            uploaded_by: uploaderId,
            is_validated: Math.random() > 0.3 // 70% de chance d'être validé
          });
      } catch (error) {
        console.warn(`Erreur création document ${doc.filename}:`, error);
      }
    }
  }

  static async createTestAppointments(projectId: string, ownerId: string, advisorId: string): Promise<void> {
    const appointments = [
      {
        title: 'Réunion de lancement du projet',
        description: 'Premier rendez-vous pour définir les objectifs et la feuille de route',
        start_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // Dans 7 jours
        duration: 90 // minutes
      },
      {
        title: 'Point d\'avancement mensuel',
        description: 'Suivi de l\'avancement du projet et résolution des blocages',
        start_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // Dans 14 jours
        duration: 60
      }
    ];

    for (const apt of appointments) {
      try {
        const endDate = new Date(apt.start_date);
        endDate.setMinutes(endDate.getMinutes() + apt.duration);

        const { data: appointment, error: aptError } = await supabase
          .from('appointments')
          .insert({
            title: apt.title,
            description: apt.description,
            start_date: apt.start_date.toISOString(),
            end_date: endDate.toISOString(),
            project_id: projectId,
            created_by: advisorId,
            status: 'PLANIFIE',
            location: 'CRESS Guadeloupe, Basse-Terre'
          })
          .select()
          .single();

        if (aptError) {
          console.warn(`Erreur création RDV ${apt.title}:`, aptError);
          continue;
        }

        // Ajouter les participants
        await supabase
          .from('appointment_attendees')
          .insert([
            {
              appointment_id: appointment.id,
              user_id: ownerId,
              is_organizer: false,
              response: 'ACCEPTED'
            },
            {
              appointment_id: appointment.id,
              user_id: advisorId,
              is_organizer: true,
              response: 'ACCEPTED'
            }
          ]);

      } catch (error) {
        console.warn(`Erreur création RDV ${apt.title}:`, error);
      }
    }
  }

  static async createTestNotifications(): Promise<void> {
    console.log('🔄 Création des notifications de test...');
    
    const notifications = [
      {
        user_email: 'marie.joseph@test.com',
        title: 'Document validé',
        message: 'Votre business plan a été approuvé par votre accompagnateur',
        type: 'DOCUMENT_VALIDATED'
      },
      {
        user_email: 'marie.joseph@test.com',
        title: 'Rendez-vous confirmé',
        message: 'Votre rendez-vous de demain à 14h a été confirmé',
        type: 'MEETING_CONFIRMED'
      },
      {
        user_email: 'sophie.martin@cress-guadeloupe.org',
        title: 'Nouveau projet assigné',
        message: 'Le projet "Ferme Agroécologique" vous a été assigné',
        type: 'PROJECT_ASSIGNED'
      }
    ];

    const { data: users } = await supabase
      .from('users')
      .select('id, email');

    const userMap = new Map(users?.map(u => [u.email, u.id]) || []);

    for (const notif of notifications) {
      const userId = userMap.get(notif.user_email);
      if (!userId) continue;

      try {
        await supabase
          .from('notifications_enhanced')
          .insert({
            user_id: userId,
            type: notif.type,
            title: notif.title,
            message: notif.message,
            is_read: Math.random() > 0.5 // 50% de chance d'être lu
          });
      } catch (error) {
        console.warn(`Erreur notification pour ${notif.user_email}:`, error);
      }
    }
  }

  static async generateAllTestData(): Promise<void> {
    try {
      console.log('🚀 Début de la génération des données de test...');
      
      await this.createTestUsers();
      await this.createTestProjects();
      await this.createTestNotifications();
      
      console.log('✅ Génération des données de test terminée !');
      console.log('📧 Comptes de test disponibles :');
      console.log('   • marie.joseph@test.com (PORTEUR - 7 projets)');
      console.log('   • sophie.martin@cress-guadeloupe.org (ACCOMPAGNATEUR)');
      console.log('   • pierre.dubois@ess-conseil.gp (ACCOMPAGNATEUR)');
      console.log('   • jean.coordinator@cress-guadeloupe.org (COORDINATEUR)');
      console.log('   • admin@cress-guadeloupe.org (ADMIN)');
      console.log('🔑 Mot de passe pour tous : password123');
      
    } catch (error) {
      console.error('❌ Erreur lors de la génération des données:', error);
      throw error;
    }
  }

  static async clearAllTestData(): Promise<void> {
    try {
      console.log('🧹 Suppression des données de test...');
      
      // Supprimer dans l'ordre inverse des dépendances
      await supabase.from('appointment_attendees').delete().neq('appointment_id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('appointments').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('documents').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('project_stages').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('steps').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('notifications_enhanced').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      await supabase.from('projects').delete().neq('id', '00000000-0000-0000-0000-000000000000');
      
      // Supprimer les utilisateurs de test
      for (const testUser of TEST_USERS) {
        try {
          await supabase.auth.admin.deleteUser(testUser.id);
          await supabase.from('users').delete().eq('id', testUser.id);
        } catch (error) {
          console.warn(`Erreur suppression ${testUser.email}:`, error);
        }
      }
      
      console.log('✅ Données de test supprimées');
    } catch (error) {
      console.error('❌ Erreur lors de la suppression:', error);
      throw error;
    }
  }
}